import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, Plus, Archive, Trash2 } from "lucide-react";

export default function PollAdmin({ api }) {
  const [polls, setPolls] = useState(null);
  const [f, setF] = useState({ question: "", a_label: "", a_sub: "", b_label: "", b_sub: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/polls");
    setPolls(data.polls || []);
  };
  useEffect(() => { load().catch(() => toast.error("Couldn't load polls.")); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    if (!f.question || !f.a_label || !f.b_label) { toast.error("Question and both option labels are required."); return; }
    setBusy(true);
    try {
      await api.post("/admin/poll", f);
      toast.success("Poll created & set active.");
      setF({ question: "", a_label: "", a_sub: "", b_label: "", b_sub: "" });
      await load();
    } catch { toast.error("Create failed."); } finally { setBusy(false); }
  };

  const setActive = async (id, active) => {
    try { await api.patch(`/admin/poll/${id}`, { active }); await load(); }
    catch { toast.error("Update failed."); }
  };

  const archive = async (id) => {
    try { await api.patch(`/admin/poll/${id}`, { archived: true }); toast.success("Poll archived."); await load(); }
    catch { toast.error("Archive failed."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this poll and its votes? This can't be undone.")) return;
    try { await api.delete(`/admin/poll/${id}`); toast.success("Poll deleted."); await load(); }
    catch { toast.error("Delete failed."); }
  };

  const field = "mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]";

  return (
    <div className="mt-8 space-y-8" data-testid="admin-poll">
      <div className="border-2 border-[#D3CFC0] p-5 space-y-4">
        <h3 className="font-head uppercase text-lg text-[#1E1E1E]">New Hot Take</h3>
        <div>
          <label className="kicker">Question</label>
          <input value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} data-testid="poll-question" className={field} placeholder="Equal playing time until high school?" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="kicker">Option A label</label>
            <input value={f.a_label} onChange={(e) => setF({ ...f, a_label: e.target.value })} data-testid="poll-a-label" className={field} placeholder="Yes." />
            <input value={f.a_sub} onChange={(e) => setF({ ...f, a_sub: e.target.value })} data-testid="poll-a-sub" className={field} placeholder="Subline (e.g. It's rec ball.)" />
          </div>
          <div>
            <label className="kicker">Option B label</label>
            <input value={f.b_label} onChange={(e) => setF({ ...f, b_label: e.target.value })} data-testid="poll-b-label" className={field} placeholder="No." />
            <input value={f.b_sub} onChange={(e) => setF({ ...f, b_sub: e.target.value })} data-testid="poll-b-sub" className={field} placeholder="Subline (e.g. Earn your minutes.)" />
          </div>
        </div>
        <button onClick={create} disabled={busy} data-testid="poll-create-btn"
          className="inline-flex items-center gap-2 bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-6 py-3 hover:bg-[#D6922C] disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Create & activate
        </button>
      </div>

      <div>
        <h3 className="font-head uppercase text-lg text-[#1E1E1E] border-b-2 border-[#D3CFC0] pb-2 mb-4">All polls</h3>
        {!polls ? (
          <div className="text-[#6E6E6E] flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : polls.length === 0 ? (
          <p className="text-sm text-[#6E6E6E]" data-testid="poll-empty">No polls yet — create one above.</p>
        ) : (
          <div className="space-y-3" data-testid="poll-list">
            {polls.map((p) => (
              <div key={p.id} data-testid={`poll-row-${p.id}`} className="border-2 border-[#D3CFC0] bg-[#E7E3D9] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {p.active && <span className="font-head uppercase text-[10px] tracking-wide bg-[#2E7D57] text-white px-2 py-0.5">Active</span>}
                      {p.archived && <span className="font-head uppercase text-[10px] tracking-wide bg-[#6E6E6E] text-white px-2 py-0.5">Archived</span>}
                      <h4 className="font-head text-[#1E1E1E] text-lg leading-tight">{p.question}</h4>
                    </div>
                    <p className="mt-1 text-sm text-[#6E6E6E]">
                      <strong>{p.a_label}</strong> — {p.a_votes} ({p.a_pct}%) · <strong>{p.b_label}</strong> — {p.b_votes} ({p.b_pct}%) · {p.total} votes
                    </p>
                  </div>
                  <div className="shrink-0 flex flex-col gap-2">
                    <button onClick={() => setActive(p.id, !p.active)} data-testid={`poll-toggle-${p.id}`}
                      className={`inline-flex items-center justify-center gap-1 font-head uppercase tracking-wide text-xs px-4 py-2 border-2 ${p.active ? "border-[#D3CFC0] text-[#6E6E6E]" : "border-[#2D2D2D] bg-[#2D2D2D] text-[#F3F1EC]"}`}>
                      {p.active ? "Deactivate" : <><Check size={13} /> Set active</>}
                    </button>
                    <div className="flex gap-2">
                      {!p.archived && (
                        <button onClick={() => archive(p.id)} data-testid={`poll-archive-${p.id}`}
                          className="flex-1 inline-flex items-center justify-center gap-1 font-head uppercase tracking-wide text-xs px-3 py-2 border-2 border-[#D3CFC0] text-[#6E6E6E] hover:border-[#2D2D2D]">
                          <Archive size={13} /> Archive
                        </button>
                      )}
                      <button onClick={() => remove(p.id)} data-testid={`poll-delete-${p.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1 font-head uppercase tracking-wide text-xs px-3 py-2 border-2 border-[#D3CFC0] text-[#6E6E6E] hover:border-[#B44] hover:text-[#B44]">
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
